import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { createSeriesSeason, fetchSeries, updateSeries, updateSeriesSeason } from '../services/vodApi'
import type { SeriesSeason, SeriesSeasonInput } from '../types/vod'
import { Button, Panel } from '../components'
import { useToast } from '../contexts/ToastContext'
import { useUnsavedChanges } from '../hooks'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './Series.module.scss'

interface SeasonDraft {
  number: string
  title: string
  year: string
}

const EMPTY_DRAFT: SeasonDraft = { number: '', title: '', year: '' }

// The series list fills an untitled season's title with its number.
const draftFor = (season: SeriesSeason): SeasonDraft => ({
  number: String(season.season_number),
  title: season.title === String(season.season_number) ? '' : season.title,
  year: season.year === null ? '' : String(season.year),
})

const parseWhole = (value: string, min: number, max: number): number | null => {
  if (!/^\d+$/.test(value.trim())) return null
  const n = Number(value)
  return n >= min && n <= max ? n : null
}

/** The draft as a request body, or an error message when a field is invalid. */
const toInput = (draft: SeasonDraft): SeriesSeasonInput | string => {
  const number = parseWhole(draft.number, 0, 9999)
  if (number === null) return 'Season number must be a whole number from 0 to 9999.'
  const year = draft.year.trim() ? parseWhole(draft.year, 1900, 2100) : null
  if (draft.year.trim() && year === null) return 'Year must be between 1900 and 2100, or left empty.'
  return { season_number: number, title: draft.title.trim() || null, year }
}

const vodsLink = (season: SeriesSeason) =>
  season.episode_count === 0 ? (
    <span className={styles.muted}>No VODs</span>
  ) : (
    <Link to={`/vods?seriesSeasonId=${season.tc_series_season_id}`}>
      View {season.episode_count} {season.episode_count === 1 ? 'VOD' : 'VODs'}
    </Link>
  )

const SeasonFields: React.FC<{
  idPrefix: string
  draft: SeasonDraft
  onChange: (draft: SeasonDraft) => void
  disabled: boolean
}> = ({ idPrefix, draft, onChange, disabled }) => (
  <>
    <div>
      <input
        id={`${idPrefix}-number`}
        aria-label="Season number"
        type="text"
        inputMode="numeric"
        value={draft.number}
        onChange={(e) => onChange({ ...draft, number: e.target.value })}
        placeholder="1 or 2026"
        disabled={disabled}
      />
    </div>
    <div>
      <input
        id={`${idPrefix}-title`}
        aria-label="Season title"
        type="text"
        value={draft.title}
        onChange={(e) => onChange({ ...draft, title: e.target.value })}
        placeholder={draft.number ? `Shown as ${draft.number}` : 'Optional'}
        disabled={disabled}
      />
    </div>
    <div>
      <input
        id={`${idPrefix}-year`}
        aria-label="Season year"
        type="text"
        inputMode="numeric"
        value={draft.year}
        onChange={(e) => onChange({ ...draft, year: e.target.value })}
        placeholder="Optional"
        disabled={disabled}
      />
    </div>
  </>
)

const SeriesDetail: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>()
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [editingSeasonId, setEditingSeasonId] = useState<string | null>(null)
  const [seasonDraft, setSeasonDraft] = useState<SeasonDraft>(EMPTY_DRAFT)
  const [addingSeason, setAddingSeason] = useState(false)
  const [newSeason, setNewSeason] = useState<SeasonDraft>(EMPTY_DRAFT)
  const [busy, setBusy] = useState(false)
  const seededFor = useRef<string | null>(null)

  const { data, error, mutate } = useSWR('vod-series', fetchSeries, { revalidateOnFocus: false })
  const series = data?.series.find((s) => s.tc_series_id === seriesId)

  useEffect(() => {
    if (series && seededFor.current !== series.tc_series_id) {
      seededFor.current = series.tc_series_id
      setTitle(series.title)
      setDescription(series.description || '')
    }
  }, [series])

  const detailsChanged =
    !!series && (title.trim() !== series.title || (description.trim() || null) !== (series.description || null))
  useUnsavedChanges(detailsChanged)

  const run = async (action: () => Promise<unknown>, success: string): Promise<boolean> => {
    setBusy(true)
    try {
      await action()
      await mutate()
      toast(success, 'success')
      return true
    } catch (err) {
      toast((err as Error).message, 'error')
      return false
    } finally {
      setBusy(false)
    }
  }

  const saveDetails = () => {
    if (!series) return
    const body: { title?: string; description?: string | null } = {}
    if (title.trim() !== series.title) body.title = title.trim()
    if ((description.trim() || null) !== (series.description || null)) body.description = description.trim() || null
    run(() => updateSeries(series.tc_series_id, body), 'Series saved')
  }

  const resetDetails = () => {
    if (!series) return
    setTitle(series.title)
    setDescription(series.description || '')
  }

  const startEdit = (season: SeriesSeason) => {
    setEditingSeasonId(season.tc_series_season_id)
    setSeasonDraft(draftFor(season))
  }

  const saveSeason = async (season: SeriesSeason) => {
    const input = toInput(seasonDraft)
    if (typeof input === 'string') return toast(input, 'error')
    const before = toInput(draftFor(season)) as SeriesSeasonInput
    const changed: Partial<SeriesSeasonInput> = {}
    if (input.season_number !== before.season_number) changed.season_number = input.season_number
    if (input.title !== before.title) changed.title = input.title
    if (input.year !== before.year) changed.year = input.year
    if (Object.keys(changed).length === 0) return setEditingSeasonId(null)
    const ok = await run(
      () => updateSeriesSeason(seriesId!, season.tc_series_season_id, changed),
      `Season ${input.season_number} saved`,
    )
    if (ok) setEditingSeasonId(null)
  }

  const addSeason = async () => {
    const input = toInput(newSeason)
    if (typeof input === 'string') return toast(input, 'error')
    const ok = await run(() => createSeriesSeason(seriesId!, input), `Season ${input.season_number} added`)
    if (ok) {
      setAddingSeason(false)
      setNewSeason(EMPTY_DRAFT)
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading series</h2>
          <p>{(error as Error).message}</p>
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading series...</h3>
        </div>
      </div>
    )
  }
  if (!series) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={listTableStyles.noResults}>
          <h3>Series not found</h3>
          <p>
            <Link to="/series">Back to all series</Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <p className={styles.muted}>
        <Link to="/series">Series</Link> › {series.title}
      </p>
      <div className={pageStyles.pageHeader}>
        <h1>{series.title}</h1>
      </div>

      <Panel>
        <Panel.Header>
          <h2>Details</h2>
        </Panel.Header>
        <div className={styles.fieldGrid}>
          <div className={formStyles.fieldGroup}>
            <label htmlFor="series-title">Title</label>
            <input
              id="series-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className={formStyles.fieldGroup}>
            <label htmlFor="series-description">Description</label>
            <textarea
              id="series-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>
        {detailsChanged && (
          <div className={styles.actionButtons}>
            <Button onClick={saveDetails} disabled={busy || !title.trim()}>
              Save
            </Button>
            <Button variant="secondary" onClick={resetDetails} disabled={busy}>
              Cancel
            </Button>
          </div>
        )}
      </Panel>

      <Panel>
        <Panel.Header>
          <h2>Seasons</h2>
        </Panel.Header>
        <p className={styles.muted}>
          A season number is a label: 1, 2, 3 or a year such as 2026. Use one convention per series.
        </p>
        <div className={`${listTableStyles.tableHeader} ${styles.seasonHeader}`}>
          <div>Number</div>
          <div>Title</div>
          <div>Year</div>
          <div>VODs</div>
          <div />
        </div>
        <div className={listTableStyles.tableBody}>
          {series.seasons.length === 0 && !addingSeason && (
            <div className={listTableStyles.noResults}>
              <p>No seasons yet.</p>
            </div>
          )}
          {series.seasons.map((season) =>
            editingSeasonId === season.tc_series_season_id ? (
              <div key={season.tc_series_season_id} className={`${listTableStyles.tableRow} ${styles.seasonRow}`}>
                <SeasonFields
                  idPrefix={`season-${season.tc_series_season_id}`}
                  draft={seasonDraft}
                  onChange={setSeasonDraft}
                  disabled={busy}
                />
                <div className={listTableStyles.cellText}>{vodsLink(season)}</div>
                <div className={styles.seasonActions}>
                  <Button size="small" onClick={() => saveSeason(season)} disabled={busy}>
                    Save
                  </Button>
                  <Button size="small" variant="secondary" onClick={() => setEditingSeasonId(null)} disabled={busy}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div key={season.tc_series_season_id} className={`${listTableStyles.tableRow} ${styles.seasonRow}`}>
                <div className={listTableStyles.cellText}>{season.season_number}</div>
                <div className={listTableStyles.cellText}>{season.title}</div>
                <div className={listTableStyles.cellText}>{season.year ?? '—'}</div>
                <div className={listTableStyles.cellText}>{vodsLink(season)}</div>
                <div className={styles.seasonActions}>
                  <Button size="small" variant="secondary" onClick={() => startEdit(season)} disabled={busy}>
                    Edit
                  </Button>
                </div>
              </div>
            ),
          )}
          {addingSeason && (
            <div className={`${listTableStyles.tableRow} ${styles.seasonRow}`}>
              <SeasonFields idPrefix="new-season" draft={newSeason} onChange={setNewSeason} disabled={busy} />
              <div />
              <div className={styles.seasonActions}>
                <Button size="small" onClick={addSeason} disabled={busy || !newSeason.number.trim()}>
                  Add
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    setAddingSeason(false)
                    setNewSeason(EMPTY_DRAFT)
                  }}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
        {!addingSeason && (
          <div className={styles.toolbar}>
            <Button variant="secondary" onClick={() => setAddingSeason(true)} disabled={busy}>
              + Add season
            </Button>
          </div>
        )}
      </Panel>
    </div>
  )
}

export default SeriesDetail
